import { useContext, useState, useRef, useEffect } from "react";
import { AppContext } from "../App";
import { callApi, getInlineLoader, getCookie } from "../Helpers";
import { FiBriefcase, FiFileText, FiUploadCloud, FiCheckCircle, FiArrowRight } from "react-icons/fi";

export default function CreateApplication({ uuid }) {
  const appContext = useContext(AppContext);

  /* Form and UI Lifecycle States */
  const [ready, setReady] = useState(true);
  const [loading, setLoading] = useState(false);

  // 1. Maintain local base64/URL state paths ONLY for UI preview targets
  const [cvPreview, setCvPreview] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const [certPreview, setCertPreview] = useState(null);
  const [data, setData] = useState(null);

  // 2. DOM reference tracking locks for the multi-part file elements
  const cvInputRef = useRef(null);
  const coverInputRef = useRef(null);
  const certInputRef = useRef(null);

  const handleFileChange = (e, previewSetter) => {
    const file = e.target?.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        // Triggers local UI visual confirmation updates safely
        previewSetter(event.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const validateForm = (cv, cover, cert) => {
    if (!cv) return "Your Curriculum Vitae (CV) file attachment is required.";
    if (!cover) return "A tailored Cover Letter file attachment is required.";
    if (!cert) return "Certified Academic Certificates are required.";
    return null;
  };

  async function init() {
    setReady(false);
    if (uuid) {
      await getData();
    }
    setReady(true);
  }

  async function getData() {
    await callApi('jobapplicant/get-job-data', { jobUuid: uuid }).then((response) => {
      if (response.status === 1) {
        setData(response.data);
      } else {
        appContext.tellError('Could not fetch data');
      }
    }).catch(() => {
      appContext.tellError('System error fetching job details');
    });
  }

  async function handleSubmitApplication() {
    // 3. FIX: Extract the structural binary Blobs instantly from memory pointers
    // BEFORE starting any asynchronous compilation logic to avoid re-render drops
    const selectedCv = cvInputRef.current?.files?.[0];
    const selectedCover = coverInputRef.current?.files?.[0];
    const selectedCert = certInputRef.current?.files?.[0];

    const error = validateForm(selectedCv, selectedCover, selectedCert);
    if (error) {
      return appContext.tellError(error);
    }

    setLoading(true);

    // 4. Build a clean payload structure ensuring valid key mappings
    const payload = {
      jobUuid: uuid,
      cv: selectedCv,
      coverLetter: selectedCover,
      certifiedCertificates: selectedCert
    };

    try {
      // 5. Fire upload stream directly via our fortified callApi helper
      const response = await callApi('jobapplicant/apply-for-job', payload);

      if (response && response.status === 1) {
        appContext.tellMessage(response.msg || "Application submitted successfully!");
        
        // Clean up memory leaks from file hooks
        setCvPreview(null);
        setCoverPreview(null);
        setCertPreview(null);
        
        // Navigate cleanly back to main tracking grid list dashboard
        appContext.navBack();
      } else {
        // Catch-all block for backend validation drops (e.g., "Please login")
        appContext.tellError(response.msg || "Submission context error.");
      }
    } catch (err) {
      appContext.tellError("Network serialization interface timeout error.");
    }

    setLoading(false);
  }

  useEffect(() => {
    appContext.setShowOverlayLoader(loading);
    return () => appContext.setShowOverlayLoader(false);
  }, [loading]);

  useEffect(() => {
    init();
  }, []);


  return (
    <>
      <style>{`
        .create-app-panel {
          font-family: 'Inter', system-ui, sans-serif;
          padding: 24px;
          background: #ffffff;
          min-height: 100vh;
        }
        .upload-dropzone {
          border: 2px dashed #e2e8f0;
          border-radius: 12px;
          padding: 24px;
          text-align: center;
          background: #f8fafc;
          cursor: pointer;
          transition: all 0.2s ease;
          margin-bottom: 20px;
        }
        .upload-dropzone:hover {
          border-color: #0d9488;
          background: #ffffff;
        }
        .file-attached-pill {
          background: #dcfce7;
          color: #166534;
          padding: 6px 12px;
          border-radius: 20px;
          font-size: 0.8rem;
          font-weight: 700;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          margin-top: 8px;
        }
        .submit-btn {
          background: #0f172a;
          color: white;
          width: 100%;
          padding: 14px;
          border: none;
          border-radius: 8px;
          font-weight: 600;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          cursor: pointer;
          transition: background 0.2s ease;
        }
        .submit-btn:hover:not(:disabled) {
          background: #0d9488;
        }
      `}</style>

      <div className="create-app-panel">
        
        {/* Header Title Grid Row Section */}
        <div className="border-bottom pb-3 mb-4 text-center">
          <h6>{data ? data.title : ''}</h6>
          <div className="d-flex align-items-center justify-content-center gap-2 mb-1">
            <FiFileText className="text-muted" size={20} />
            <h4 className="fw-bold mb-0" style={{ color: '#0f172a' }}>Submit Application</h4>
          </div>
          <p className="text-muted small mb-0">Upload all required files to finalize your application registry process.</p>
        </div>

        <div className="row g-1">
          
          {/* UPLOAD SLOTh 1: Curriculum Vitae (CV) */}
          <div className="col-12 mb-3">
            <label className="text-uppercase small fw-bold text-muted d-block mb-2">Curriculum Vitae (CV)</label>
            <div className="upload-dropzone" onClick={() => cvInputRef.current.click()}>
              <FiUploadCloud size={28} className="text-muted mb-2" />
              <div className="small fw-semibold text-dark">Click to upload your professional CV file</div>
              <p className="text-muted px-2 small mb-0">PDF or Word Documents accepted</p>
              
              {cvPreview && (
                <div className="file-attached-pill">
                  <FiCheckCircle /> CV File Document Staged
                </div>
              )}
            </div>
            <input type="file" ref={cvInputRef} hidden accept=".pdf,.doc,.docx" onChange={(e) => handleFileChange(e, setCvPreview)} />
          </div>

          {/* UPLOAD SLOT 2: Cover Letter */}
          <div className="col-12 mb-3">
            <label className="text-uppercase small fw-bold text-muted d-block mb-2">Cover Letter</label>
            <div className="upload-dropzone" onClick={() => coverInputRef.current.click()}>
              <FiUploadCloud size={28} className="text-muted mb-2" />
              <div className="small fw-semibold text-dark">Click to attach your Cover Letter</div>
              <p className="text-muted px-2 small mb-0">PDF or Word Documents accepted</p>
              
              {coverPreview && (
                <div className="file-attached-pill">
                  <FiCheckCircle /> Cover Letter Staged
                </div>
              )}
            </div>
            <input type="file" ref={coverInputRef} hidden accept=".pdf,.doc,.docx" onChange={(e) => handleFileChange(e, setCoverPreview)} />
          </div>

          {/* UPLOAD SLOT 3: Certified Certificates */}
          <div className="col-12 mb-4">
            <label className="text-uppercase small fw-bold text-muted d-block mb-2">Certified Academic Certificates</label>
            <div className="upload-dropzone" onClick={() => certInputRef.current.click()}>
              <FiUploadCloud size={28} className="text-muted mb-2" />
              <div className="small fw-semibold text-dark">Click to upload certified certificate</div>
              <p className="text-muted px-2 small mb-0">PDF or Word Documents Accepted</p>
              
              {certPreview && (
                <div className="file-attached-pill">
                  <FiCheckCircle /> Academic Certificate Staged
                </div>
              )}
            </div>
            <input type="file" ref={certInputRef} hidden accept=".pdf,.doc,.docx" onChange={(e) => handleFileChange(e, setCertPreview)} />
          </div>

          {/* Final Action Submission Processor Button Row */}
          <div className="col-12 mt-2">
            <button 
              className="submit-btn" 
              onClick={handleSubmitApplication}
              disabled={loading}
            >
              {loading ? "Please Wait..." : "SUBMIT APPLICATION VACANCY"} <FiArrowRight />
            </button>
          </div>

        </div>
      </div>
    </>
  );
}